/**
 * Comments in the editor: threads anchored to a box on the plan (drawn with the comment tool, C), replies,
 * resolve and reopen, and @mentions of the company's members (autocomplete; mentioned people get an email that
 * opens the editor at the thread). Threads belong to the design and show on every version; each says the version
 * it was started on. Anyone who can open the editor can comment; resolving someone else's thread needs edit.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, MessageSquare, RotateCcw, Send } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg } from "../api/org";
import { Badge, Button, Spinner, useToast } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { shownVersion } from "../lib/format";
import { filterMembers, insertMention, mentionedIds, openMention, splitMentions, type Member } from "./mentions.ts";
import type { Box } from "./viewport.ts";

type Thread = S["DesignCommentThread"];

export function useCommentThreads(designId: string) {
  return useQuery({
    queryKey: ["comments", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/comments", { params: { path: { design_id: designId } } })),
    refetchInterval: 30_000,
  });
}

function useMembers(designId: string) {
  return useQuery({
    queryKey: ["comment-members", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/comment-members", { params: { path: { design_id: designId } } })),
    staleTime: 5 * 60_000,
  });
}

// ------------------------------------------------------------------------------------------ mention input
export function MentionInput({
  members,
  value,
  onChange,
  onSubmit,
  placeholder,
  autoFocus,
}: {
  members: Member[];
  value: { text: string; picked: Member[] };
  onChange: (v: { text: string; picked: Member[] }) => void;
  onSubmit: () => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);
  const at = openMention(value.text, caret, dismissed);
  const open = at ? filterMembers(members, at.query) : [];

  const pick = (m: Member) => {
    if (!at) return;
    const next = insertMention(value.text, at, m);
    setDismissed(at.start); // the list closes on its pick (a new "@" opens it again)
    onChange({ text: next.text, picked: value.picked.some((p) => p.user_id === m.user_id) ? value.picked : [...value.picked, m] });
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(next.caret, next.caret);
      setCaret(next.caret);
    });
  };

  return (
    <div className="mention-input">
      <textarea
        ref={ref}
        className="input"
        rows={3}
        value={value.text}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-label={placeholder}
        aria-autocomplete="list"
        aria-expanded={open.length > 0}
        onChange={(e) => {
          onChange({ ...value, text: e.target.value });
          setCaret(e.target.selectionStart);
          setActive(0);
        }}
        onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart)}
        onKeyDown={(e) => {
          if (open.length) {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => (i + (e.key === "ArrowDown" ? 1 : open.length - 1)) % open.length);
              return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              pick(open[active] ?? open[0]!);
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setDismissed(at!.start);
              return;
            }
          }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      {open.length > 0 && (
        <ul className="mention-list" role="listbox">
          {open.map((m, i) => (
            <li
              key={m.user_id ?? m.name}
              role="option"
              aria-selected={i === active}
              className={i === active ? "on" : ""}
              onPointerDown={(e) => {
                e.preventDefault();
                pick(m);
              }}
            >
              <b>{m.name}</b>
              {m.email && <span className="muted small" dir="auto">{m.email}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Body({ text, mentions }: { text: string; mentions: S["CommentPerson"][] }) {
  return (
    <p className="comment-body" dir="auto">
      {splitMentions(text, mentions.map((m) => m.name)).map((part, i) => (part.mention ? <mark key={i}>{part.text}</mark> : <span key={i}>{part.text}</span>))}
    </p>
  );
}

// ------------------------------------------------------------------------------------------------ panel
export function CommentsPanel({
  designId,
  version,
  versionOffset,
  threads,
  loading,
  active,
  onActive,
  pendingBox,
  onCancelPending,
  showResolved,
  onShowResolved,
}: {
  designId: string;
  version: number;
  /** `version_offset` of the design (see shownVersion) */
  versionOffset?: number | null;
  threads: Thread[];
  loading: boolean;
  active: string | null;
  onActive: (id: string | null) => void;
  pendingBox: Box | null;
  onCancelPending: () => void;
  showResolved: boolean;
  onShowResolved: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const tr = useT();
  const intl = useIntlShape();
  const when = (value: string) => intl.formatDate(value, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const { me, can } = useOrg();
  const members = useMembers(designId).data?.items ?? [];
  const [draft, setDraft] = useState<{ text: string; picked: Member[] }>({ text: "", picked: [] });
  const [reply, setReply] = useState<{ text: string; picked: Member[] }>({ text: "", picked: [] });
  const refresh = (t: Thread) => {
    qc.setQueryData(["comments", designId], (old: S["DesignCommentThreadList"] | undefined) => ({
      items: old ? (old.items.some((x) => x.id === t.id) ? old.items.map((x) => (x.id === t.id ? t : x)) : [...old.items, t]) : [t],
    }));
  };
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/designs/{design_id}/comments", {
          params: { path: { design_id: designId } },
          body: {
            anchor: { type: "box", min_xy: pendingBox!.min, max_xy: pendingBox!.max },
            version_number: version,
            body: draft.text,
            mentions: mentionedIds(draft.text, draft.picked),
          },
        }),
      ),
    onSuccess: (t) => {
      refresh(t);
      setDraft({ text: "", picked: [] });
      onCancelPending();
      onActive(t.id);
      const n = t.comments[0]?.mentions.length ?? 0;
      toast(tr("editor.comments.posted", { count: n }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const answer = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/v1/designs/{design_id}/comments/{thread_id}/replies", {
          params: { path: { design_id: designId, thread_id: id } },
          body: { body: reply.text, mentions: mentionedIds(reply.text, reply.picked) },
        }),
      ),
    onSuccess: (t) => {
      refresh(t);
      setReply({ text: "", picked: [] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: S["DesignCommentThreadStatus"] }) =>
      unwrap(api.PATCH("/v1/designs/{design_id}/comments/{thread_id}", { params: { path: { design_id: designId, thread_id: id } }, body: { status } })),
    onSuccess: (t) => refresh(t),
    onError: (e) => toast(errorMessage(e), true),
  });

  const visible = useMemo(() => threads.filter((t) => showResolved || t.status === "open" || t.id === active), [threads, showResolved, active]);
  const thread = threads.find((t) => t.id === active) ?? null;
  const index = (t: Thread) => threads.filter((x) => showResolved || x.status === "open" || x.id === active).indexOf(t) + 1;
  const mayResolve = (t: Thread) => can("designs_leads", "edit") || t.created_by.user_id === me.user.id;

  if (pendingBox)
    return (
      <section className="stack">
        <div className="side-card">
          <b>{tr("editor.comments.new.title")}</b>
          <p className="muted small">{tr("editor.comments.new.help", { version: shownVersion(version, versionOffset) })}</p>
          <MentionInput members={members} value={draft} onChange={setDraft} onSubmit={() => draft.text.trim() && create.mutate()} placeholder={tr("editor.comments.new.placeholder")} autoFocus />
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <Button size="sm" variant="ghost" onClick={onCancelPending}>
              {tr("editor.comments.cancel")}
            </Button>
            <Button size="sm" variant="primary" icon={<Send size={14} />} disabled={!draft.text.trim()} loading={create.isPending} onClick={() => create.mutate()}>
              {tr("editor.comments.post")}
            </Button>
          </div>
        </div>
      </section>
    );

  if (thread)
    return (
      <section className="stack">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <Button size="sm" variant="ghost" icon={<DirIcon><ArrowLeft size={14} /></DirIcon>} onClick={() => onActive(null)}>
            {tr("editor.comments.all")}
          </Button>
          <span className="row" style={{ gap: 6 }}>
            <Badge tone={thread.status === "open" ? "info" : "ok"}>{thread.status === "open" ? tr("editor.comments.status.open") : tr("editor.comments.status.resolved")}</Badge>
            <span className="muted small">{tr("editor.comments.version", { version: shownVersion(thread.version_number, versionOffset) })}</span>
          </span>
        </div>
        <ol className="comment-thread">
          {thread.comments.map((c) => (
            <li key={c.id}>
              <div className="comment-head">
                <span className="comment-avatar">{c.author.name.slice(0, 1).toUpperCase()}</span>
                <b>{c.author.name}</b>
                <span className="muted small">{when(c.created_at)}</span>
              </div>
              <Body text={c.body} mentions={c.mentions} />
            </li>
          ))}
        </ol>
        {thread.status === "resolved" && thread.resolved_by && (
          <p className="muted small">
            {thread.resolved_at ? tr("editor.comments.resolvedByOn", { name: thread.resolved_by.name, date: when(thread.resolved_at) }) : tr("editor.comments.resolvedBy", { name: thread.resolved_by.name })}
          </p>
        )}
        <MentionInput members={members} value={reply} onChange={setReply} onSubmit={() => reply.text.trim() && answer.mutate(thread.id)} placeholder={thread.status === "resolved" ? tr("editor.comments.reply.placeholderReopens") : tr("editor.comments.reply.placeholder")} />
        <div className="row" style={{ justifyContent: "space-between" }}>
          {mayResolve(thread) ? (
            thread.status === "open" ? (
              <Button size="sm" icon={<Check size={14} />} loading={setStatus.isPending} onClick={() => setStatus.mutate({ id: thread.id, status: "resolved" })}>
                {tr("editor.comments.resolve")}
              </Button>
            ) : (
              <Button size="sm" icon={<RotateCcw size={14} />} loading={setStatus.isPending} onClick={() => setStatus.mutate({ id: thread.id, status: "open" })}>
                {tr("editor.comments.reopen")}
              </Button>
            )
          ) : (
            <span />
          )}
          <Button size="sm" variant="primary" icon={<Send size={14} />} disabled={!reply.text.trim()} loading={answer.isPending} onClick={() => answer.mutate(thread.id)}>
            {tr("editor.comments.reply")}
          </Button>
        </div>
      </section>
    );

  return (
    <section className="stack">
      <p className="muted small">{intl.formatMessage({ id: "editor.comments.help" }, { kbd: (c) => <kbd>{c}</kbd> })}</p>
      <label className="row small" style={{ gap: 6 }}>
        <input type="checkbox" checked={showResolved} onChange={(e) => onShowResolved(e.target.checked)} /> {tr("editor.comments.showResolved")}
      </label>
      {loading ? (
        <Spinner label={tr("editor.loading")} />
      ) : visible.length === 0 ? (
        <div className="empty-mini">
          <MessageSquare size={18} /> {tr("editor.comments.empty", { resolved: String(showResolved) })}
        </div>
      ) : (
        <ul className="comment-list">
          {visible.map((t) => {
            const first = t.comments[0];
            return (
              <li key={t.id} className={t.status === "resolved" ? "resolved" : ""} onClick={() => onActive(t.id)}>
                <span className="comment-index">{index(t)}</span>
                <div>
                  <div className="row" style={{ gap: 6 }}>
                    <b>{first?.author.name}</b>
                    {t.kind === "note" && <Badge tone="info">{tr("editor.comments.sketchNote")}</Badge>}
                    <span className="muted small">{tr("editor.comments.versionDate", { version: shownVersion(t.version_number, versionOffset), date: when(t.updated_at) })}</span>
                  </div>
                  <div className="small ellipsis-2">{first?.body}</div>
                  {t.comments.length > 1 && <div className="muted small">{tr("editor.comments.replies", { count: t.comments.length - 1 })}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
